import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../../API';
import UnifiedStainSelector from '../../../../../components/definition/UnifiedStainSelector';
import FocusImage from '../../../../../widgets/FocusImage';
import { girderThumbnailUrl } from '../../../../../utils/girderThumbnailUrl';
import HelpPopover from '../../../../../components/blocks/HelpPopover';
import RegionMembershipList from '../../../../../components/blocks/RegionMembershipList';
import RegionDetailsSubview from '../../../../../components/blocks/views/RegionDetailsSubview';
import StainDetailsSubview from '../../../../../components/blocks/views/StainDetailsSubview';
import {
    formatRegionChipLabel,
    regionDisplayName,
} from '../../../../../components/blocks/utils/formatRegion';
import { validateItemForApprove } from '../../fileUpload/utils/validation';
import ConfirmDialog from '../components/ConfirmDialog';
import SlideAssignmentDrawer from '../components/SlideAssignmentDrawer';
import SlideBlockPicker from './SlideBlockPicker';
import {
    isQuarantined,
    canEditSlide,
    formatMagnificationDisplay,
    regionsMatchBlock,
    stainLabel,
} from './slideStatus';
import styles from './slides.module.css';
import enhancedStyles from '../CaseBuilderEnhanced.module.css';

function titleCaseLabel(name) {
    const s = String(name || '');
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export default function SlideAssignSheet({
    open,
    item,
    caseBlocks = [],
    caseQuarantined = false,
    stains = [],
    updateItemMetadata,
    metadataPending = false,
    onClose,
    onUnquarantine,
    unquarantinePending = false,
    onPlaceInQuarantine,
    placeInQuarantinePending = false,
    onMoveToStaging,
}) {
    const [thumbError, setThumbError] = useState(false);
    const [enlarged, setEnlarged] = useState(null);
    const [editingRegion, setEditingRegion] = useState(null);
    const [editingStain, setEditingStain] = useState(false);
    const [regionDirty, setRegionDirty] = useState(false);
    const [stainDirty, setStainDirty] = useState(false);
    const [discardConfirm, setDiscardConfirm] = useState(null); // { kind: 'region'|'stain', next: 'back'|'close' }
    const [requarantineConfirm, setRequarantineConfirm] = useState(false);
    const [closeWaiting, setCloseWaiting] = useState(false);
    const [pendingBlock, setPendingBlock] = useState(null);
    const [pendingClear, setPendingClear] = useState(false);
    const [pendingRestore, setPendingRestore] = useState(false);
    const [assocErrors, setAssocErrors] = useState(() => new Set());
    const regionActionsRef = useRef(null);
    const stainActionsRef = useRef(null);

    const moveToStagingButton = onMoveToStaging ? (
        <button
            type="button"
            className={enhancedStyles.buttonSecondary}
            onClick={() => onMoveToStaging(item)}
        >
            Move to staging
        </button>
    ) : null;

    const itemId = item?._id;
    const bdsa = item?.bdsaData || {};
    const slideQuarantined = isQuarantined(item);
    const canEdit = canEditSlide(item, caseQuarantined);
    const validation = item ? validateItemForApprove(item) : { isValid: false, errors: [] };
    const selectedBlock = useMemo(
        () => (caseBlocks || []).find((b) => String(b._id) === String(bdsa.blockId)) || null,
        [caseBlocks, bdsa.blockId],
    );
    const currentRegions = Array.isArray(bdsa.regions) ? bdsa.regions : [];
    const regionsCustomized = !!selectedBlock && !regionsMatchBlock(currentRegions, selectedBlock);
    const currentStain = bdsa.stain || null;
    const stainName = stainLabel(item) || currentStain?.name || null;
    const matchHints = bdsa.filenameMatchHints || {};
    const blockSuggestions = (!selectedBlock && matchHints.block?.status === 'ambiguous')
        ? (matchHints.block.candidates || [])
        : [];
    const stainSuggestions = (!currentStain && matchHints.stain?.status === 'ambiguous')
        ? (matchHints.stain.candidates || [])
        : [];

    const applyStainSuggestion = (stain) => {
        if (!canEdit || !stain) return;
        updateItemMetadata(itemId, { stain });
    };

    const associatedQuery = useQuery({
        queryKey: [itemId, 'tiles-images'],
        queryFn: () => api.get(`item/${itemId}/tiles/images`),
        enabled: open && !!itemId,
    });

    const associatedNames = useMemo(() => {
        const list = associatedQuery.data?.data;
        return Array.isArray(list) ? list : [];
    }, [associatedQuery.data]);

    const previewNames = associatedNames.filter((name) => {
        const lower = String(name).toLowerCase();
        return lower === 'label' || lower === 'macro' || lower.includes('label') || lower.includes('macro');
    });

    useEffect(() => {
        if (!editingRegion) setRegionDirty(false);
    }, [editingRegion]);

    useEffect(() => {
        if (!editingStain) setStainDirty(false);
    }, [editingStain]);

    // Full sheet close (or item change) always returns to the main assignment view.
    useEffect(() => {
        if (open) return;
        setEditingRegion(null);
        setEditingStain(false);
        setRegionDirty(false);
        setStainDirty(false);
        setDiscardConfirm(null);
        setRequarantineConfirm(false);
        setCloseWaiting(false);
        setPendingBlock(null);
        setPendingClear(false);
        setPendingRestore(false);
        setEnlarged(null);
        setThumbError(false);
        setAssocErrors(new Set());
    }, [open]);

    useEffect(() => {
        setEditingRegion(null);
        setEditingStain(false);
        setRegionDirty(false);
        setStainDirty(false);
        setDiscardConfirm(null);
        setRequarantineConfirm(false);
        setCloseWaiting(false);
        setThumbError(false);
        setAssocErrors(new Set());
    }, [itemId]);

    // After metadata PUTs settle, decide whether an incomplete released slide must re-quarantine.
    useEffect(() => {
        if (!closeWaiting || metadataPending) return;
        setCloseWaiting(false);
        if (item && !isQuarantined(item) && !validateItemForApprove(item).isValid) {
            setRequarantineConfirm(true);
            return;
        }
        onClose?.();
    }, [closeWaiting, metadataPending, item, onClose]);

    if (!open || !item) return null;

    const applyBlock = (block) => {
        updateItemMetadata(itemId, {
            blockId: block?._id ?? null,
            regions: Array.isArray(block?.regionDefinitions) ? block.regionDefinitions : [],
        });
        setPendingBlock(null);
        setPendingClear(false);
        setPendingRestore(false);
    };

    const restoreFromBlock = () => {
        if (!selectedBlock) return;
        applyBlock(selectedBlock);
    };

    const clearBlock = () => {
        updateItemMetadata(itemId, { blockId: null, regions: [] });
        setPendingClear(false);
    };

    const handleSelectBlock = (block) => {
        if (!canEdit || !block) return;
        if (String(bdsa.blockId || '') === String(block._id)) return;
        if (regionsCustomized) {
            setPendingBlock(block);
            return;
        }
        applyBlock(block);
    };

    const handleClearBlock = () => {
        if (!canEdit) return;
        if (regionsCustomized) {
            setPendingClear(true);
            return;
        }
        clearBlock();
    };

    const handleRemoveRegion = (region, index) => {
        if (!canEdit) return;
        const next = currentRegions.filter((_, i) => i !== index);
        updateItemMetadata(itemId, { regions: next });
    };

    const handleSaveRegionDetails = (modified) => {
        if (!editingRegion) return;
        const next = [...currentRegions];
        next[editingRegion.index] = modified;
        updateItemMetadata(itemId, { regions: next });
        setRegionDirty(false);
        setEditingRegion(null);
    };

    const handleSaveStainDetails = (modified) => {
        updateItemMetadata(itemId, { stain: modified });
        setStainDirty(false);
        setEditingStain(false);
    };

    const leaveRegionDetails = () => {
        setDiscardConfirm(null);
        setRegionDirty(false);
        setEditingRegion(null);
    };

    const leaveStainDetails = () => {
        setDiscardConfirm(null);
        setStainDirty(false);
        setEditingStain(false);
    };

    const requestLeaveRegion = () => {
        if (regionDirty && canEdit) {
            setDiscardConfirm({ kind: 'region', next: 'back' });
            return;
        }
        leaveRegionDetails();
    };

    const requestLeaveStain = () => {
        if (stainDirty && canEdit) {
            setDiscardConfirm({ kind: 'stain', next: 'back' });
            return;
        }
        leaveStainDetails();
    };

    const attemptSheetClose = () => {
        if (closeWaiting || requarantineConfirm) return;
        if (metadataPending) {
            setCloseWaiting(true);
            return;
        }
        if (!slideQuarantined && !validation.isValid) {
            setRequarantineConfirm(true);
            return;
        }
        onClose?.();
    };

    const requestClose = () => {
        if (closeWaiting) return;
        if (editingRegion && regionDirty && canEdit) {
            setDiscardConfirm({ kind: 'region', next: 'close' });
            return;
        }
        if (editingStain && stainDirty && canEdit) {
            setDiscardConfirm({ kind: 'stain', next: 'close' });
            return;
        }
        attemptSheetClose();
    };

    const confirmDiscard = () => {
        const next = discardConfirm?.next;
        if (discardConfirm?.kind === 'stain') {
            leaveStainDetails();
        } else {
            leaveRegionDetails();
        }
        if (next === 'close') attemptSheetClose();
    };

    const confirmRequarantine = async () => {
        const ok = await onPlaceInQuarantine?.(item);
        if (ok === false) return;
        setRequarantineConfirm(false);
        onClose?.();
    };

    const level = Math.max(1, parseInt(bdsa.level, 10) || 1);
    const scan = Math.max(1, parseInt(bdsa.scan, 10) || 1);

    const blockName = selectedBlock?.name || selectedBlock?.longName || null;
    const restoreLabel = blockName
        ? `Reset to Block ${blockName}'s regions`
        : 'Reset to this block’s regions';

    const onRegionDetails = !!editingRegion;
    const onStainDetails = !!editingStain;
    const onDetails = onRegionDetails || onStainDetails;
    const detailDirty = onRegionDetails ? regionDirty : stainDirty;

    let footer;
    if (onDetails) {
        footer = (
            <>
                <button
                    type="button"
                    className={enhancedStyles.buttonSecondary}
                    onClick={onRegionDetails ? requestLeaveRegion : requestLeaveStain}
                >
                    {detailDirty && canEdit ? 'Discard' : (canEdit ? 'Cancel' : 'Back')}
                </button>
                {canEdit && (
                    <button
                        type="button"
                        className={enhancedStyles.buttonPrimary}
                        onClick={() => {
                            if (onRegionDetails) regionActionsRef.current?.save?.();
                            else stainActionsRef.current?.save?.();
                        }}
                    >
                        Save changes
                    </button>
                )}
            </>
        );
    } else if (slideQuarantined) {
        footer = (
            <>
                <span className={styles.footerStatus}>Currently in quarantine</span>
                {moveToStagingButton}
                <button
                    type="button"
                    className={enhancedStyles.buttonPrimary}
                    disabled={!validation.isValid || unquarantinePending}
                    onClick={() => onUnquarantine?.(item)}
                >
                    {unquarantinePending ? 'Releasing…' : 'Release slide'}
                </button>
            </>
        );
    } else if (canEdit) {
        footer = (
            <>
                <span className={styles.footerStatus}>Released — case still quarantined</span>
                {moveToStagingButton}
                <button
                    type="button"
                    className={enhancedStyles.buttonSecondary}
                    disabled={placeInQuarantinePending || !onPlaceInQuarantine}
                    onClick={() => onPlaceInQuarantine?.(item)}
                >
                    {placeInQuarantinePending ? 'Placing…' : 'Place in quarantine'}
                </button>
            </>
        );
    } else {
        footer = (
            <>
                <span className={styles.footerStatus}>Published — view only</span>
                {moveToStagingButton}
                <button
                    type="button"
                    className={enhancedStyles.buttonSecondary}
                    disabled={placeInQuarantinePending || !onPlaceInQuarantine}
                    onClick={() => onPlaceInQuarantine?.(item)}
                >
                    {placeInQuarantinePending ? 'Placing…' : 'Place in quarantine'}
                </button>
            </>
        );
    }

    const sheetSubtitle = onDetails
        ? null
        : (canEdit ? 'Assign a block and stain.' : 'Published — view only.');

    return (
        <>
            <SlideAssignmentDrawer
                open={open}
                title={item.name || 'Slide'}
                subtitle={sheetSubtitle}
                onClose={requestClose}
                footer={footer}
            >
                {editingRegion ? (
                    <RegionDetailsSubview
                        region={editingRegion.region}
                        blockName={blockName}
                        scope="slide"
                        onBack={requestLeaveRegion}
                        onSave={handleSaveRegionDetails}
                        readOnly={!canEdit}
                        onDirtyChange={setRegionDirty}
                        actionsRef={regionActionsRef}
                    />
                ) : editingStain && currentStain ? (
                    <StainDetailsSubview
                        stain={currentStain}
                        onBack={requestLeaveStain}
                        onSave={handleSaveStainDetails}
                        readOnly={!canEdit}
                        onDirtyChange={setStainDirty}
                        actionsRef={stainActionsRef}
                    />
                ) : (
                    <>
                        <section className={styles.sheetSection}>
                            <h4 className={styles.sheetSectionTitle}>Preview</h4>
                            <div className={styles.sheetSectionBody}>
                                <div className={styles.previewGrid}>
                                    <div className={styles.previewCard}>
                                        <span className={styles.previewLabel}>Thumbnail</span>
                                        {!thumbError ? (
                                            <img
                                                className={styles.previewImg}
                                                src={girderThumbnailUrl(api, itemId)}
                                                alt="Thumbnail"
                                                onError={() => setThumbError(true)}
                                                onClick={() => setEnlarged({ type: 'wsi', value: itemId })}
                                            />
                                        ) : (
                                            <div className={styles.thumbFallback}>No thumbnail</div>
                                        )}
                                    </div>
                                    {previewNames.map((name) => {
                                        const src = api.url(`item/${itemId}/tiles/images/${name}`);
                                        if (assocErrors.has(name)) return null;
                                        return (
                                            <div key={name} className={styles.previewCard}>
                                                <span className={styles.previewLabel}>{titleCaseLabel(name)}</span>
                                                <img
                                                    className={styles.previewImg}
                                                    src={src}
                                                    alt={titleCaseLabel(name)}
                                                    onError={() => setAssocErrors((prev) => new Set(prev).add(name))}
                                                    onClick={() => setEnlarged({ type: 'src', value: src })}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    type="button"
                                    className={enhancedStyles.buttonSecondary}
                                    onClick={() => setEnlarged({ type: 'wsi', value: itemId })}
                                >
                                    Open full viewer
                                </button>
                            </div>
                        </section>

                        <section className={styles.sheetSection}>
                            <h4 className={styles.sheetSectionTitle}>Block</h4>
                            <div className={styles.sheetSectionBody}>
                                <SlideBlockPicker
                                    blocks={caseBlocks}
                                    selectedBlock={selectedBlock}
                                    onSelect={handleSelectBlock}
                                    onClear={handleClearBlock}
                                    disabled={!canEdit}
                                    readOnly={!canEdit}
                                />
                                {!selectedBlock && blockSuggestions.length > 0 && (
                                    <div>
                                        <p className={styles.suggestionHint}>
                                            Multiple block patterns matched this filename. Pick one or adjust patterns in block setup.
                                        </p>
                                        <div className={styles.suggestionList}>
                                            {blockSuggestions.map((candidate) => (
                                                <div key={String(candidate._id)} className={styles.suggestionRow}>
                                                    <span className={styles.suggestionName}>{candidate.name || 'Block'}</span>
                                                    {candidate.filenamePattern && (
                                                        <code className={styles.suggestionPattern}>{candidate.filenamePattern}</code>
                                                    )}
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            className={enhancedStyles.buttonSecondary}
                                                            onClick={() => applyBlock(candidate)}
                                                        >
                                                            Apply
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {selectedBlock && (
                                    <div className={styles.nestedRegions}>
                                        <span className={styles.nestedRegionsLabel}>Regions</span>
                                        {regionsCustomized && (
                                            <p className={styles.blockHint}>Customized for this slide</p>
                                        )}
                                        <RegionMembershipList
                                            items={currentRegions.map((region, index) => ({
                                                key: region._id || index,
                                                label: formatRegionChipLabel(region)
                                                    || regionDisplayName(region)
                                                    || region.abbreviation
                                                    || region.displayName
                                                    || 'Region',
                                                region,
                                                index,
                                            }))}
                                            emptyText="No regions on this slide."
                                            onConfigure={(row) => {
                                                if (!canEdit) return;
                                                setEditingRegion({
                                                    region: row.region,
                                                    index: row.index,
                                                });
                                            }}
                                            onRemove={(row) => handleRemoveRegion(row.region, row.index)}
                                            configureTitle="Details for this slide"
                                            removeTitle="Remove from this slide"
                                            disabled={!canEdit}
                                        />
                                        {canEdit && regionsCustomized && (
                                            <button
                                                type="button"
                                                className={enhancedStyles.linkButton}
                                                onClick={() => setPendingRestore(true)}
                                            >
                                                {restoreLabel}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className={styles.sheetSection}>
                            <h4 className={styles.sheetSectionTitle}>Stain</h4>
                            <div className={styles.sheetSectionBody}>
                                {currentStain ? (
                                    <RegionMembershipList
                                        items={[{
                                            key: currentStain._id || 'stain',
                                            label: stainName || 'Stain',
                                        }]}
                                        onConfigure={() => {
                                            if (!canEdit) return;
                                            setEditingStain(true);
                                        }}
                                        onRemove={() => {
                                            if (!canEdit) return;
                                            updateItemMetadata(itemId, { stain: null });
                                        }}
                                        configureTitle="Details for this slide"
                                        removeTitle="Remove stain"
                                        disabled={!canEdit}
                                    />
                                ) : canEdit ? (
                                    <>
                                        {stainSuggestions.length > 0 && (
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <p className={styles.suggestionHint}>
                                                    Multiple stain patterns matched this filename.
                                                </p>
                                                <div className={styles.suggestionList}>
                                                    {stainSuggestions.map((candidate) => (
                                                        <div key={String(candidate._id)} className={styles.suggestionRow}>
                                                            <span className={styles.suggestionName}>{candidate.name || 'Stain'}</span>
                                                            {candidate.filenamePattern && (
                                                                <code className={styles.suggestionPattern}>{candidate.filenamePattern}</code>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className={enhancedStyles.buttonSecondary}
                                                                onClick={() => applyStainSuggestion(candidate)}
                                                            >
                                                                Apply
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        <UnifiedStainSelector
                                            stains={stains}
                                            selectedStain={null}
                                            onSelectStain={(stain) => updateItemMetadata(itemId, { stain })}
                                            compact
                                            emptyLabel="Choose stain…"
                                        />
                                    </>
                                ) : (
                                    <span className={styles.metaLine}>—</span>
                                )}
                            </div>
                        </section>

                        <section className={styles.sheetSection}>
                            <h4 className={styles.sheetSectionTitle}>Slide details</h4>
                            <div className={styles.sheetSectionBody}>
                                <div className={styles.numberRow}>
                                    <div className={styles.compactField}>
                                        <div className={styles.formLabelRow}>
                                            <label className={styles.formLabel} htmlFor={`slide-section-${itemId}`}>
                                                Section #
                                            </label>
                                            <HelpPopover label="About section number" title="Section #">
                                                The first section from a block is 1. Use 2 or higher for deeper
                                                sections or recuts from the same tissue.
                                            </HelpPopover>
                                        </div>
                                        <input
                                            id={`slide-section-${itemId}`}
                                            className={styles.numberInput}
                                            type="number"
                                            min={1}
                                            value={level}
                                            disabled={!canEdit}
                                            onChange={(e) => updateItemMetadata(itemId, { level: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                                        />
                                    </div>
                                    <div className={styles.compactField}>
                                        <div className={styles.formLabelRow}>
                                            <label className={styles.formLabel} htmlFor={`slide-scan-${itemId}`}>
                                                Scan #
                                            </label>
                                            <HelpPopover label="About scan number" title="Scan #">
                                                The first digitization of a section is 1. Use 2 or higher for
                                                rescans of the same physical slide.
                                            </HelpPopover>
                                        </div>
                                        <input
                                            id={`slide-scan-${itemId}`}
                                            className={styles.numberInput}
                                            type="number"
                                            min={1}
                                            value={scan}
                                            disabled={!canEdit}
                                            onChange={(e) => updateItemMetadata(itemId, { scan: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                                        />
                                    </div>
                                    <div className={styles.compactField}>
                                        <div className={styles.formLabelRow}>
                                            <label className={styles.formLabel} htmlFor={`slide-mag-${itemId}`}>
                                                Magnification
                                            </label>
                                            <HelpPopover label="About magnification" title="Magnification">
                                                Read from the image file. Not editable here. Unknown means the
                                                file did not report a usable objective magnification.
                                            </HelpPopover>
                                        </div>
                                        <input
                                            id={`slide-mag-${itemId}`}
                                            className={`${styles.numberInput} ${styles.readOnlyInput}`}
                                            type="text"
                                            value={formatMagnificationDisplay(bdsa.magnification)}
                                            disabled
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {!validation.isValid && canEdit && slideQuarantined && (
                            <div className={styles.validationBox}>
                                <strong>Needed before release:</strong>
                                <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.2rem' }}>
                                    {validation.errors.map((err) => <li key={err}>{err}</li>)}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </SlideAssignmentDrawer>

            <ConfirmDialog
                open={!!pendingBlock}
                title="Replace slide regions?"
                message="This slide has customized regions. Re-picking the block will reset regions from the block."
                confirmLabel="Reset regions"
                variant="danger"
                onConfirm={() => applyBlock(pendingBlock)}
                onCancel={() => setPendingBlock(null)}
            />

            <ConfirmDialog
                open={pendingRestore}
                title={restoreLabel}
                message={blockName
                    ? `This will replace this slide’s regions with the regions from block ${blockName}.`
                    : 'This will replace this slide’s regions with the regions from the block.'}
                confirmLabel="Reset regions"
                variant="danger"
                onConfirm={restoreFromBlock}
                onCancel={() => setPendingRestore(false)}
            />

            <ConfirmDialog
                open={pendingClear}
                title="Clear block?"
                message="This slide has customized regions. Clearing the block will remove the block and all regions from this slide."
                confirmLabel="Clear block"
                variant="danger"
                onConfirm={clearBlock}
                onCancel={() => setPendingClear(false)}
            />

            <ConfirmDialog
                open={!!discardConfirm}
                title={discardConfirm?.kind === 'stain' ? 'Discard stain changes?' : 'Discard region changes?'}
                message={discardConfirm?.kind === 'stain'
                    ? 'You have unsaved stain details. Discard them?'
                    : 'You have unsaved region details. Discard them?'}
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                variant="danger"
                onConfirm={confirmDiscard}
                onCancel={() => setDiscardConfirm(null)}
            />

            <ConfirmDialog
                open={requarantineConfirm}
                title="Place slide in quarantine?"
                message={
                    validation.errors.length
                        ? `This released slide no longer meets release requirements (${validation.errors.join('; ')}). Place it back in quarantine, or cancel to keep editing.`
                        : 'This released slide no longer meets release requirements. Place it back in quarantine, or cancel to keep editing.'
                }
                confirmLabel={placeInQuarantinePending ? 'Placing…' : 'Place in quarantine'}
                cancelLabel="Cancel"
                variant="danger"
                confirmDisabled={placeInQuarantinePending || !onPlaceInQuarantine}
                onConfirm={confirmRequarantine}
                onCancel={() => setRequarantineConfirm(false)}
            />

            {closeWaiting && (
                <div className={enhancedStyles.dialogOverlay} role="status" aria-live="polite">
                    <div className={enhancedStyles.dialog}>
                        <h3 className={enhancedStyles.dialogTitle}>Saving…</h3>
                        <p className={enhancedStyles.dialogBody}>
                            Waiting for slide updates to finish before closing.
                        </p>
                    </div>
                </div>
            )}

            {enlarged?.type === 'wsi' && (
                <FocusImage wsi={enlarged.value} clearState={() => setEnlarged(null)} style={{ zIndex: 1100 }} />
            )}
            {enlarged?.type === 'src' && (
                <FocusImage src={enlarged.value} clearState={() => setEnlarged(null)} style={{ zIndex: 1100 }} />
            )}
        </>
    );
}

